// src/support/campaignRollup.ts
//
// Server rollups for the Leader Campaigns tab. `db/campaign/deliveries-rollup/` and
// `db/campaign/enrollment-rollup/` aggregate over a manager's whole org subtree on the server (no
// 1,000-row page cap, no browser-side org walk of every delivery). These pure helpers turn their
// responses into the shapes the dashboard and team table already use (`DeliveryMetrics`,
// `PipelineMetrics`, `EnrollmentMetrics`, `CompletionCohort`).
//
// `members` in both responses is a flat list of every OWNER in scope with that person's OWN counts, so a
// manager's team figure is the sum over the manager's subtree (`subtreeFiguresFromMembers`).

import { computeSubtreeScope, type OrgChartRow } from './orgScope';
import { DELIVERY_STATUSES, type DeliveryStatus } from './campaigns';
import {
  emptyDeliveryMetrics,
  emptyPipelineMetrics,
  type DateWindow,
  type DeliveryMetrics,
  type PipelineMetrics,
} from './campaignLeader';
import { emptyEnrollmentMetrics, type EnrollmentMetrics } from './campaignEnrollmentLeader';
import type { CompletionCohort } from './campaignSalesLevels';

export interface DeliveriesRollupStatusBucket {
  status: string;
  count: number;
  no_opp: number;
  pipeline: number;
}

export interface DeliveriesRollupMember {
  email: string;
  total: number;
  active: number;
  pipeline: number;
  /** Every known status (zero-filled); absent on a server that predates per-member status counts. */
  by_status?: Record<string, number>;
}

export interface DeliveriesRollupResponse {
  campaign?: number;
  /** The applied window, echoed back; absent on a server that predates the date parameters. */
  from?: string;
  to?: string;
  total: number;
  active: number;
  total_pipeline: number;
  pipeline_available: boolean;
  by_status: DeliveriesRollupStatusBucket[];
  members: DeliveriesRollupMember[];
}

export interface EnrollmentRollupMember {
  email: string;
  total_enrolled: number;
  not_started: number;
  in_progress: number;
  completed: number;
  avg_completion: number;
}

export interface EnrollmentRollupResponse {
  campaign?: number;
  total_enrolled: number;
  not_started: number;
  in_progress: number;
  completed: number;
  avg_completion: number;
  members: EnrollmentRollupMember[];
}

const STATUSES: DeliveryStatus[] = DELIVERY_STATUSES.map((s) => s.value);
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * Whether a deliveries-rollup response can be trusted for what was asked. A server that predates the date
 * window ignores `from`/`to` and answers all-time (no echo), and one that predates per-member status counts
 * has no `by_status` on its members; either would mislabel its figures, so callers fall back to the
 * browser-side calculation instead.
 */
export function deliveriesRollupUsable(
  resp: DeliveriesRollupResponse | null | undefined,
  window: DateWindow | null | undefined,
): boolean {
  if (!resp || !Array.isArray(resp.by_status) || !Array.isArray(resp.members)) return false;
  if (!resp.members.every((m) => m.by_status != null)) return false;
  if (window) return resp.from === window.start && resp.to === window.end;
  return resp.from == null && resp.to == null;
}

/** Delivery counts by status for the dashboard (same shape `sumDeliveries` produces). */
export function deliveryMetricsFromRollup(resp: DeliveriesRollupResponse): DeliveryMetrics {
  const metrics = emptyDeliveryMetrics();
  metrics.total = num(resp.total);
  metrics.active = num(resp.active);
  for (const b of resp.by_status) {
    if (STATUSES.includes(b.status as DeliveryStatus)) metrics.byStatus[b.status as DeliveryStatus] = num(b.count);
  }
  return metrics;
}

/** Pipeline $ / session counts by status for the pipeline table (same shape `pipelineByStatus` produces). */
export function pipelineMetricsFromRollup(resp: DeliveriesRollupResponse): PipelineMetrics {
  const metrics = emptyPipelineMetrics();
  metrics.total = num(resp.total_pipeline);
  metrics.totalCount = num(resp.total);
  for (const b of resp.by_status) {
    const status = b.status as DeliveryStatus;
    if (!STATUSES.includes(status)) continue;
    metrics.byStatus[status] = num(b.pipeline);
    metrics.countByStatus[status] = num(b.count);
    metrics.noOppByStatus[status] = num(b.no_opp);
  }
  return metrics;
}

/** The rollup's owners keyed by lower-cased email. */
export function rollupMembersByEmail(resp: DeliveriesRollupResponse): Map<string, DeliveriesRollupMember> {
  const out = new Map<string, DeliveriesRollupMember>();
  for (const m of resp.members) out.set(String(m.email || '').trim().toLowerCase(), m);
  return out;
}

/**
 * A person's team figure: their own plus everyone under them in the org tree, summed from the rollup's
 * per-owner members. An IC (no reports) gets just their own.
 */
export function subtreeFiguresFromMembers(
  email: string,
  orgRows: OrgChartRow[],
  members: Map<string, DeliveriesRollupMember>,
): { metrics: DeliveryMetrics; pipelineTotal: number } {
  const metrics = emptyDeliveryMetrics();
  let pipelineTotal = 0;
  for (const scoped of computeSubtreeScope(orgRows, email).emails) {
    const m = members.get(scoped.toLowerCase());
    if (!m) continue;
    metrics.total += num(m.total);
    metrics.active += num(m.active);
    pipelineTotal += num(m.pipeline);
    for (const s of STATUSES) metrics.byStatus[s] += num(m.by_status?.[s]);
  }
  return { metrics, pipelineTotal };
}

/** Enablement counts for the dashboard (same shape `sumEnrollments` produces). */
export function enrollmentMetricsFromRollup(resp: EnrollmentRollupResponse): EnrollmentMetrics {
  const metrics = emptyEnrollmentMetrics();
  metrics.total = num(resp.total_enrolled);
  metrics.byStatus.not_started = num(resp.not_started);
  metrics.byStatus.in_progress = num(resp.in_progress);
  metrics.byStatus.completed = num(resp.completed);
  metrics.completed = num(resp.completed);
  metrics.avgPercent = Math.round(num(resp.avg_completion));
  return metrics;
}

/**
 * The rollup's per-user enrollment counts as completion cohorts, so the team table's existing cohort
 * machinery (`managerUsersCompleted`, `icJourneyMaps` degrading to completed/enrolled) works for
 * all-campaigns too, not just one campaign. `percent_sum` is rebuilt from the mean so cohort sums average
 * correctly.
 */
export function cohortsFromEnrollmentRollup(resp: EnrollmentRollupResponse): CompletionCohort[] {
  return resp.members.map((m) => ({
    sl1: '',
    sl2: '',
    sl3: '',
    email: String(m.email || '').trim().toLowerCase(),
    enrolled: num(m.total_enrolled),
    not_started: num(m.not_started),
    in_progress: num(m.in_progress),
    completed: num(m.completed),
    percent_sum: num(m.avg_completion) * num(m.total_enrolled),
  }));
}
